import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, styled, Tooltip } from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridColumnVisibilityModel,
    GridPaginationModel,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import Icon from '@mdi/react';
import { mdiTransitConnectionVariant } from '@mdi/js';

import { useAppSelector } from '../../state/hooks';
import { Lane } from '../../interfaces/services/lanes';
import { getLaneId } from '../../utils/stateHelpers';
import { formatNumber, formatCurrency, formatNumberPercent } from '../../utils/formattingUtils';
import { buildLaneLink } from '../../utils/navigationHelpers';
import { ApiStatus } from '../../utils/enums';
import { useFilters } from '../Filters/useFilters';
import TableHeader from './TableHeader';

const classesPrefix = 'laneTable';

const classes = {
    root: `${classesPrefix}-root`,
    tableContents: `${classesPrefix}-tableContents`,
    laneRow: `${classesPrefix}-laneRow`,
    hoveredLaneRow: `${classesPrefix}-hoveredLaneRow`,
};

const StyledDiv = styled('div')(({ theme }) => {
    return {
        [`&.${classes.root}`]: {
            borderRadius: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            height: '100%',
        },
        [`& .${classes.tableContents}`]: {
            height: '300px',
            flexGrow: 1,
            position: 'relative',
            '& .MuiDataGrid-root .MuiDataGrid-cell:focus': {
                outline: 0,
            },
            '& .MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                outline: 0,
            },
            '& .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                outline: 0,
            },
        },
        [`& .${classes.laneRow}`]: {
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
        },
        [`& .${classes.hoveredLaneRow}`]: {
            backgroundColor: theme.palette.action.hover,
        },
    };
});

const LaneTable = ({
    adjusting,
    isExpanded,
    hoveredLaneId,
    handleHoveredLaneChange,
    toggleTableExpansion,
}: {
    adjusting: boolean;
    isExpanded: boolean;
    /** Hovered lane id to highlight */
    hoveredLaneId: string | null;
    /** Function to handle hovering a lane */
    handleHoveredLaneChange: (laneId: string | null) => void;
    toggleTableExpansion: (isExpanded: boolean) => void;
}): React.JSX.Element => {
    const navigate = useNavigate();
    const filters = useFilters();

    const apiRef = useGridApiRef();

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        origin_area: isExpanded,
        lane: !isExpanded,
        destination_area: isExpanded,
        loaded_distance_miles_amount: isExpanded,
        lane_yield_usd: isExpanded,
        total_revenue_usd_amount: isExpanded,
        empty_distance_miles_amount: isExpanded,
        empty_mile_pct: isExpanded,
        mph: isExpanded,
        rph: isExpanded,
        netwise_in_transit_delay_hours_duration: isExpanded,
        pickup_party_count: isExpanded,
    });

    const laneApiStatus = useAppSelector((state) => { return state.lanes.laneApiStatus; });
    const lanes = useAppSelector((state) => { return state.lanes.lanes; });
    const laneCount = useAppSelector((state) => { return state.lanes.laneCount; });

    useEffect(() => {
        setColumnVisibilityModel({
            origin_area: isExpanded,
            lane: !isExpanded,
            destination_area: isExpanded,
            loaded_distance_miles_amount: isExpanded,
            lane_yield_usd: isExpanded,
            total_revenue_usd_amount: isExpanded,
            empty_distance_miles_amount: isExpanded,
            empty_mile_pct: isExpanded,
            mph: isExpanded,
            rph: isExpanded,
            netwise_in_transit_delay_hours_duration: isExpanded,
            pickup_party_count: isExpanded,
        });
    }, [isExpanded]);

    const isLaneLoading = laneApiStatus === ApiStatus.Idle || laneApiStatus === ApiStatus.Loading;

    const onRowMouseOver = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
        const laneId = event.currentTarget.dataset.id;
        handleHoveredLaneChange(laneId || null);
    };

    const onRowMouseLeave = (): void => {
        handleHoveredLaneChange(null);
    };

    const activateLane = (origin: string, destination: string): void => {
        navigate(buildLaneLink(origin, destination, filters));
    };

    const exportCsv = (allColumns: boolean): void => {
        if (!isExpanded && !allColumns) {
            apiRef.current.exportDataAsCsv({
                fileName: 'Network Insights - Lanes',
                fields: ['lane', 'freight_order_count'],
            });
        } else {
            apiRef.current.exportDataAsCsv({
                fileName: 'Network Insights - Lanes',
                allColumns,
            });
        }
    };

    const handleToggleExpansion = (newIsExpanded: boolean): void => {
        toggleTableExpansion(newIsExpanded);
    };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    const columns = useMemo((): GridColDef<Lane>[] => {
        return [
            {
                headerName: 'Lane',
                field: 'lane',
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <Tooltip
                            title={
                                `${row.pickup_service_area_name ||
                                row.pickup_service_area_id
                                } > ${row.delivery_service_area_name ||
                                row.delivery_service_area_id
                                }`
                            }
                        >
                            <Grid
                                container
                                spacing={{ xs: 1, md: 3 }}
                                wrap='nowrap'
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Grid item>
                                    {row.pickup_service_area_name || row.pickup_service_area_id}
                                </Grid>
                                <Grid item>
                                    <Icon path={mdiTransitConnectionVariant} size={0.7} />
                                </Grid>
                                <Grid item>
                                    {row.delivery_service_area_name || row.delivery_service_area_id}
                                </Grid>
                            </Grid>
                        </Tooltip>
                    );
                },
                valueGetter: (_value, row): string => {
                    return `${row.pickup_service_area_name || row.pickup_service_area_id} -> ${row.delivery_service_area_name || row.delivery_service_area_id}`;
                },
                disableColumnMenu: true,
                sortable: false,
                filterable: false,
                disableReorder: true,
                disableExport: true,
                flex: 2,
                minWidth: 250,
            },
            {
                headerName: 'Origin',
                field: 'origin_area',
                valueGetter: (_value, row): string => {
                    return row.pickup_service_area_name || row.pickup_service_area_id || '';
                },
                flex: 1,
                minWidth: 200,
            },
            {
                headerName: 'Destination',
                field: 'destination_area',
                valueGetter: (_value, row): string => {
                    return row.delivery_service_area_name || row.delivery_service_area_id || '';
                },
                flex: 1,
                minWidth: 200,
            },
            {
                headerName: 'Volume',
                field: 'freight_order_count',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                align: !isExpanded ? 'center' : 'right',
                headerAlign: !isExpanded ? 'center' : 'right',
                type: 'number',
                width: 125,
                disableColumnMenu: !isExpanded,
            },
            {
                headerName: 'Total RPM',
                field: 'total_revenue_usd_amount',
                description: 'Total Rate Per Mile',
                valueGetter: (_value, row): number | null => {
                    return row.total_revenue_usd_amount !== null && row.loaded_distance_miles_amount ?
                        row.total_revenue_usd_amount / row.loaded_distance_miles_amount :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                width: 145,
                type: 'number',
            },
            {
                headerName: 'LOH',
                field: 'loaded_distance_miles_amount',
                description: 'Average Length of Haul',
                valueGetter: (_value, row): number | null => {
                    return row.loaded_distance_miles_amount !== null && row.freight_order_count ?
                        row.loaded_distance_miles_amount / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                width: 110,
                type: 'number',
            },
            {
                headerName: 'Yield',
                field: 'lane_yield_usd',
                description: 'Network based margin per load per day',
                valueFormatter: (value): string => {
                    return formatCurrency(value, 0, 0);
                },
                width: 110,
                type: 'number',
            },
            {
                headerName: 'Empty Miles',
                field: 'empty_distance_miles_amount',
                valueGetter: (_value, row): number | null => {
                    return row.empty_distance_miles_amount !== null && row.freight_order_count ?
                        row.empty_distance_miles_amount / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 1, 1);
                },
                flex: 1,
                type: 'number',
                minWidth: 145,
            },
            {
                headerName: 'Empty Mile %',
                field: 'empty_mile_pct',
                valueGetter: (_value, row): number | null => {
                    return row.empty_distance_miles_amount !== null && row.travel_distance_miles_amount
                        ? (row.empty_distance_miles_amount / row.travel_distance_miles_amount) * 100
                        : null;
                },
                valueFormatter: (value): string => {
                    return formatNumberPercent(value, 0, 0);
                },
                flex: 1,
                type: 'number',
                minWidth: 145,
            },
            {
                headerName: 'MPH',
                field: 'mph',
                description: 'Miles Per Hour',
                valueGetter: (_value, row): number | null => {
                    return row.loaded_distance_miles_amount !== null && row.first_stop_departure_to_final_stop_arrival_hours_duration ?
                        row.loaded_distance_miles_amount / row.first_stop_departure_to_final_stop_arrival_hours_duration :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                flex: 1,
                type: 'number',
                minWidth: 110,
            },
            {
                headerName: 'RPH',
                field: 'rph',
                description: 'Revenue Per Hour (in USD)',
                valueGetter: (_value, row): number | null => {
                    return row.total_revenue_usd_amount !== null && row.first_stop_departure_to_final_stop_arrival_hours_duration
                        ? row.total_revenue_usd_amount / row.first_stop_departure_to_final_stop_arrival_hours_duration
                        : null;
                },
                valueFormatter: (value): string => {
                    return formatCurrency(value);
                },
                flex: 1,
                type: 'number',
                minWidth: 110,
            },
            {
                headerName: 'In Transit Delay',
                field: 'netwise_in_transit_delay_hours_duration',
                description: 'In Transit Delay (in hours)',
                valueGetter: (_value, row): number | null => {
                    return row.netwise_in_transit_delay_hours_duration !== null && row.freight_order_count ?
                        row.netwise_in_transit_delay_hours_duration / row.freight_order_count :
                        null;
                },
                valueFormatter: (value): string => {
                    return formatNumber(value, 2, 2);
                },
                flex: 1,
                type: 'number',
                minWidth: 150,
            },
            {
                headerName: 'Shippers',
                field: 'pickup_party_count',
                valueFormatter: (value): string => {
                    return formatNumber(value, 0, 0);
                },
                flex: 1,
                minWidth: 90,
                type: 'number',
            },
        ];
    }, [isExpanded]);

    return (
        <StyledDiv className={classes.root}>
            <TableHeader
                title='Lanes'
                itemCount={laneCount}
                loading={isLaneLoading}
                adjusting={adjusting}
                expanded={isExpanded}
                toggleExpansion={handleToggleExpansion}
                handleDownload={exportCsv}
            />
            <div className={classes.tableContents}>
                <DataGridPremium
                    classes={{
                        row: classes.laneRow,
                    }}
                    apiRef={apiRef}
                    rows={lanes}
                    // rowCount={laneCount}
                    columns={columns}
                    density='compact'
                    disableRowSelectionOnClick
                    disableRowGrouping
                    disableAggregation
                    loading={isLaneLoading || adjusting}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel): void => {
                        setColumnVisibilityModel(newModel);
                    }}
                    paginationModel={{
                        page,
                        pageSize,
                    }}
                    onPaginationModelChange={(model: GridPaginationModel): void => {
                        setPage(model.page);
                        setPageSize(model.pageSize);
                    }}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    pagination
                    initialState={{
                        density: 'compact',
                    }}
                    disableColumnSelector={adjusting}
                    getRowId={(row): string => {
                        return getLaneId(row);
                    }}
                    getRowClassName={(params): string => {
                        return params.id === hoveredLaneId ? classes.hoveredLaneRow : '';
                    }}
                    slotProps={{
                        row: {
                            onMouseOver: onRowMouseOver,
                            onMouseLeave: onRowMouseLeave,
                        },
                        columnsManagement: {
                            searchPredicate: columnSearchPredicate,
                        },
                    }}
                    onRowClick={(params): void => {
                        activateLane(
                            params.row.pickup_service_area_id,
                            params.row.delivery_service_area_id
                        );
                    }}
                />
            </div>
        </StyledDiv>
    );
};

export default LaneTable;
